import { useEffect, useState } from 'react';

import auth from '../services/authService';
import { estimateSongSeconds, isFullyCharted } from '../helpers/serviceLength';
import { loadFullSongs } from '../utils/songCache';

// The last length worked out for each song, so a list rebuilt on coming back shows it at once
// rather than popping it in after the saved songs are read, which shifts what sits beside it
const lastSeconds = new Map();

const remembered = (songs) =>
  new Map(songs.filter((song) => lastSeconds.has(song._id)).map((song) => [song._id, lastSeconds.get(song._id)]));

/**
 * How long each fully charted song plays for. Songs that aren't fully charted are left out, as their
 * length would only be a guess.
 *
 * @param {Array} songs - Songs off the team list
 * @returns {{ lengths: Map, fromCache: boolean }} - Seconds by song id, and whether any came from
 *   out-of-date saved copies because the fetch failed
 */
const useSongLengths = (songs) => {
  const charted = songs.filter(isFullyCharted);
  const [state, setState] = useState(() => ({ lengths: remembered(charted), fromCache: false }));
  // Re-run when a song is edited as well as when the songs change
  const versionKey = charted.map((song) => `${song._id}@${song.updatedAt}`).join(',');

  useEffect(() => {
    if (charted.length === 0) {
      setState({ lengths: new Map(), fromCache: false });
      return undefined;
    }

    let cancelled = false;
    // Lengths already known stay up while the rest are worked out
    setState((prev) => ({ ...prev, lengths: remembered(charted) }));

    loadFullSongs(charted, auth.getCurrentUser()).then(({ songs: fullSongs, fromCache }) => {
      if (cancelled) return;

      const lengths = new Map();
      fullSongs.forEach((song) => {
        const seconds = estimateSongSeconds(song);

        if (seconds) {
          lengths.set(song._id, seconds);
          lastSeconds.set(song._id, seconds);
        } else {
          lastSeconds.delete(song._id);
        }
      });

      setState({ lengths, fromCache });
    });

    return () => {
      cancelled = true;
    };
    // versionKey stands in for the songs, which are a new array on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionKey]);

  return state;
};

export default useSongLengths;
